import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdmin } from "@/lib/superadmin";

/** Quick-jump destinations for the header search. */
const DESTINATIONS = [
  { label: "Dashboard", path: "/superadmin/dashboard", icon: "ri-dashboard-3-line", keywords: "overview home stats" },
  { label: "Analytics & BI", path: "/superadmin/analytics", icon: "ri-line-chart-line", keywords: "charts reports insights revenue" },
  { label: "Tenants", path: "/superadmin/tenants", icon: "ri-store-2-line", keywords: "restaurants merchants outlets impersonate" },
  { label: "Menu Oversight", path: "/superadmin/menu", icon: "ri-restaurant-line", keywords: "items dishes categories" },
  { label: "Subscriptions", path: "/superadmin/billing", icon: "ri-bank-card-line", keywords: "billing invoices payments" },
  { label: "Financial Ops", path: "/superadmin/finance", icon: "ri-money-rupee-circle-line", keywords: "finance payouts settlements" },
  { label: "Voided Bills", path: "/superadmin/voided", icon: "ri-delete-bin-6-line", keywords: "voided cancelled deleted bills recycle bin restore" },
  { label: "Plans & Pricing", path: "/superadmin/plans", icon: "ri-price-tag-3-line", keywords: "tiers pricing free pro" },
  { label: "Feature Control", path: "/superadmin/entitlements", icon: "ri-toggle-line", keywords: "entitlements flags features gating" },
  { label: "Coupons & Promos", path: "/superadmin/coupons", icon: "ri-coupon-3-line", keywords: "discounts offers codes" },
  { label: "Announcements", path: "/superadmin/announcements", icon: "ri-megaphone-line", keywords: "broadcast admins banners" },
  { label: "System & Security", path: "/superadmin/system", icon: "ri-shield-check-line", keywords: "audit logs security health" },
];

const CRUMB_LABELS: Record<string, string> = Object.fromEntries(
  DESTINATIONS.map((d) => [d.path.split("/").pop() as string, d.label]),
);

export default function Header({ onMenuClick }: { onMenuClick: () => void }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { me, signOut } = useAdmin();

  const initials =
    (me.name || me.email)
      .split(/[@\s.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "AD";

  const section = location.pathname.split("/")[2] ?? "dashboard";
  const crumb = CRUMB_LABELS[section] ?? "Dashboard";

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DESTINATIONS;
    return DESTINATIONS.filter(
      (d) =>
        d.label.toLowerCase().includes(q) || d.keywords.toLowerCase().includes(q),
    );
  }, [query]);

  // Close popovers on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Ctrl/Cmd+K opens search
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const go = (path: string) => {
    navigate(path);
    setSearchOpen(false);
    setQuery("");
    setHighlight(0);
  };

  return (
    <header className="h-16 bg-background-50/80 backdrop-blur border-b border-background-200/80 flex items-center justify-between px-4 md:px-6 shrink-0 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuClick}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 transition-colors lg:hidden cursor-pointer shrink-0"
          aria-label="Open menu"
        >
          <i className="ri-menu-line text-lg"></i>
        </button>

        {/* Breadcrumb */}
        <nav className="hidden sm:flex items-center gap-1.5 text-sm min-w-0">
          <span className="text-foreground-400">Superadmin</span>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="font-semibold text-foreground-900 truncate">{crumb}</span>
        </nav>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Quick search */}
        <div className="relative" ref={searchRef}>
          <button
            onClick={() => {
              setSearchOpen(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            className="flex items-center gap-2 h-9 px-3 rounded-lg text-foreground-500 hover:text-foreground-700 bg-background-100/70 hover:bg-background-100 border border-background-200/60 transition-colors cursor-pointer"
          >
            <i className="ri-search-line"></i>
            <span className="hidden md:inline text-xs">Jump to…</span>
            <kbd className="hidden md:inline px-1.5 py-0.5 rounded border border-background-300 bg-background-50 text-[10px] font-mono text-foreground-400">
              Ctrl K
            </kbd>
          </button>

          {searchOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden z-50">
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-background-100">
                <i className="ri-search-line text-foreground-400"></i>
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlight(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlight((h) => Math.min(h + 1, results.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlight((h) => Math.max(h - 1, 0));
                    } else if (e.key === "Enter" && results[highlight]) {
                      go(results[highlight].path);
                    }
                  }}
                  placeholder="Search panels…"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <ul className="max-h-72 overflow-y-auto py-1">
                {results.length ? (
                  results.map((d, i) => (
                    <li key={d.path}>
                      <button
                        onClick={() => go(d.path)}
                        onMouseEnter={() => setHighlight(i)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors cursor-pointer ${
                          i === highlight
                            ? "bg-primary-50 text-primary-700"
                            : "text-foreground-700"
                        }`}
                      >
                        <i className={`${d.icon} text-base text-foreground-400`}></i>
                        {d.label}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="px-3 py-4 text-center text-xs text-foreground-400">
                    No matching panels
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Status */}
        <div className="hidden lg:flex items-center gap-2 h-9 px-3 rounded-lg bg-emerald-50 border border-emerald-100">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
          <span className="text-[11px] text-emerald-700 font-semibold">Operational</span>
        </div>

        {/* Profile */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center ring-2 ring-primary-100">
              <span className="text-xs font-bold text-background-50">{initials}</span>
            </div>
            <i
              className={`ri-arrow-down-s-line text-sm text-foreground-500 transition-transform hidden md:block ${
                dropdownOpen ? "rotate-180" : ""
              }`}
            ></i>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-background-100">
                <p className="text-sm font-semibold text-foreground-900 truncate">
                  {me.name || "Admin"}
                </p>
                <p className="text-xs text-foreground-400 truncate">{me.email}</p>
              </div>
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/superadmin/announcements");
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-settings-line text-foreground-400"></i>
                Admin accounts
              </button>
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/superadmin/system");
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-settings-3-line text-foreground-400"></i>
                System settings
              </button>
              <hr className="my-1 border-background-100" />
              <button
                onClick={() => void signOut()}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-box-r-line"></i>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
